/**
 * LES APPELS DU MODULE ANALYSE — ouvert au lot 96, 18/09/2026.
 *
 * Même contrat que `api.ts`, dont il reprend `appeler` et `echec` : les refus
 * se reconnaissent à leur code, la phrase de l'API est gardée quand elle
 * existe.
 */

import { appeler, echec, type Resultat } from './api.js';
import type { FacturesLues } from './factures.js';
import type {
  CoteEcheancier,
  EcheancierLu,
  PaiementPrevu,
  PieceEcheancier,
} from './echeancier.js';
import type { BfrLu, ReglagesAnalyse } from './bfr.js';
import type { PrevisionsLues } from './previsions.js';
import type { DroitsLus, EtatSource, MorceauLu, RessourceSource } from './sources.js';
import type { RegleLue, TresorerieLue } from './tresorerie.js';
import type { ResultatLu } from './sig.js';
import type { RentabiliteLue } from './rentabilite.js';
import type { IndicateursLus } from './indicateurs.js';
import type { FiabiliteLue } from './fiabilite.js';

export async function lireEtatDesSources(
  jeton: string,
): Promise<Resultat<readonly EtatSource[]>> {
  const echange = await appeler('/analyse/pennylane', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { ressources: readonly EtatSource[] }).ressources,
  };
}

export async function lireDroitsPennylane(jeton: string): Promise<Resultat<DroitsLus>> {
  const echange = await appeler('/analyse/pennylane/droits', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as DroitsLus };
}

export async function lireUnMorceauPennylane(
  jeton: string,
  ressource: RessourceSource,
  suite: string | null,
): Promise<Resultat<MorceauLu>> {
  const echange = await appeler('/analyse/pennylane/lectures', jeton, {
    methode: 'POST',
    corps: suite === null ? { ressource } : { ressource, suite },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as MorceauLu };
}

// ---------------------------------------------------------------------------
// L'échéancier — lot 97
// ---------------------------------------------------------------------------

export async function lireEcheancier(
  jeton: string,
  cote: CoteEcheancier,
): Promise<Resultat<EcheancierLu>> {
  const echange = await appeler(`/analyse/echeancier/${cote}`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as EcheancierLu };
}

export interface HistoriqueDate {
  readonly avant: readonly PaiementPrevu[];
  readonly apres: readonly PaiementPrevu[];
  readonly commentaire: string | null;
  readonly par: string;
  readonly le: string;
}

export interface ReglementDuTiers {
  readonly numero: string | null;
  readonly date_piece: string | null;
  readonly echeance: string | null;
  readonly reglee_le: string;
  readonly montant_ttc: string;
}

export interface DetailPiece {
  readonly historique: readonly HistoriqueDate[];
  readonly reglements: readonly ReglementDuTiers[];
}

export async function lireDetailPiece(
  jeton: string,
  cote: CoteEcheancier,
  id: string,
): Promise<Resultat<DetailPiece>> {
  const echange = await appeler(`/analyse/echeancier/${cote}/${id}`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as DetailPiece };
}

export async function poserPaiements(
  jeton: string,
  cote: CoteEcheancier,
  id: string,
  paiements: readonly PaiementPrevu[],
  commentaire: string | null,
): Promise<Resultat<PieceEcheancier>> {
  const echange = await appeler(`/analyse/echeancier/${cote}/${id}`, jeton, {
    methode: 'PUT',
    corps: {
      paiements: paiements.map((p) =>
        p.montant === null ? { date: p.date } : { montant: p.montant, date: p.date },
      ),
      commentaire,
    },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: (echange.corps as { piece: PieceEcheancier }).piece };
}

export async function poserEnGroupe(
  jeton: string,
  cote: CoteEcheancier,
  pieces: readonly string[],
  date: string | null,
): Promise<
  Resultat<{ modifiees: number; prelevements: number; introuvables: number }>
> {
  const echange = await appeler(`/analyse/echeancier/${cote}`, jeton, {
    methode: 'PUT',
    corps: { pieces, date },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: echange.corps as {
      modifiees: number;
      prelevements: number;
      introuvables: number;
    },
  };
}

export async function poserCession(
  jeton: string,
  id: string,
  cedeeLe: string | null,
): Promise<Resultat<null>> {
  const echange = await appeler(`/analyse/cessions/${id}`, jeton, {
    methode: 'PUT',
    corps: { cedee_le: cedeeLe },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export async function reglerFournisseur(
  jeton: string,
  id: string,
  reglage: {
    readonly prelevement?: boolean | null;
    readonly centre_id?: string | null;
  },
): Promise<Resultat<null>> {
  const echange = await appeler(`/analyse/fournisseurs/${id}`, jeton, {
    methode: 'PUT',
    corps: reglage,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// Les prévisions — lot 98
// ---------------------------------------------------------------------------

export async function lirePrevisions(jeton: string): Promise<Resultat<PrevisionsLues>> {
  const echange = await appeler('/analyse/previsions', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as PrevisionsLues };
}

export async function enregistrerPrevision(
  jeton: string,
  id: string | null,
  corps: Record<string, unknown>,
): Promise<Resultat<null>> {
  const echange = await appeler(
    id === null ? '/analyse/previsions' : `/analyse/previsions/${id}`,
    jeton,
    { methode: id === null ? 'POST' : 'PUT', corps },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export async function supprimerPrevision(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/analyse/previsions/${id}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export interface EvenementPrevision {
  readonly date_rythme: string | null;
  readonly action: string;
  readonly details: Readonly<Record<string, unknown>>;
  readonly par: string;
  readonly le: string;
}

export async function lireHistoriquePrevision(
  jeton: string,
  id: string,
): Promise<Resultat<readonly EvenementPrevision[]>> {
  const echange = await appeler(`/analyse/previsions/${id}/historique`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { historique: EvenementPrevision[] }).historique,
  };
}

export type GesteLigne =
  'decaler' | 'supprimer' | 'retablir' | 'rapprocher' | 'defaire' | 'refuser';

export async function gesteSurLigne(
  jeton: string,
  previsionId: string,
  dateRythme: string,
  geste: GesteLigne,
  corps?: Record<string, unknown>,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/analyse/previsions/${previsionId}/lignes/${dateRythme}/${geste}`,
    jeton,
    corps === undefined ? { methode: 'POST' } : { methode: 'POST', corps },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// Le BFR et les réglages de l'analyse — lot 99
// ---------------------------------------------------------------------------

export async function lireBfr(jeton: string): Promise<Resultat<BfrLu>> {
  const echange = await appeler('/analyse/bfr', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as BfrLu };
}

export async function lireReglagesAnalyse(
  jeton: string,
): Promise<Resultat<ReglagesAnalyse>> {
  const echange = await appeler('/analyse/reglages', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as ReglagesAnalyse };
}

export async function enregistrerReglagesAnalyse(
  jeton: string,
  corps: Record<string, number | string>,
): Promise<Resultat<ReglagesAnalyse>> {
  const echange = await appeler('/analyse/reglages', jeton, { methode: 'PUT', corps });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as ReglagesAnalyse };
}

// ---------------------------------------------------------------------------
// La trésorerie — lot 100
// ---------------------------------------------------------------------------

export async function lireTresorerie(jeton: string): Promise<Resultat<TresorerieLue>> {
  const echange = await appeler('/analyse/tresorerie', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as TresorerieLue };
}

export async function ajouterRegleTresorerie(
  jeton: string,
  motif: string,
  nature: string,
): Promise<Resultat<RegleLue>> {
  const echange = await appeler('/analyse/tresorerie/regles', jeton, {
    methode: 'POST',
    corps: { motif, nature },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as RegleLue };
}

export async function retirerRegleTresorerie(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/analyse/tresorerie/regles/${id}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// Le résultat — lot 101
// ---------------------------------------------------------------------------

export async function lireResultat(jeton: string): Promise<Resultat<ResultatLu>> {
  const echange = await appeler('/analyse/resultat', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as ResultatLu };
}

export interface IssueBalance {
  readonly mois: string;
  readonly issue: 'complete' | 'refusee' | 'injoignable';
  readonly lignes: number;
  readonly ecartees: number;
  readonly message: string | null;
  readonly champs_recus: readonly string[] | null;
}

export async function lireBalanceDuMois(
  jeton: string,
  mois: string,
): Promise<Resultat<IssueBalance>> {
  const echange = await appeler('/analyse/resultat/balance', jeton, {
    methode: 'POST',
    corps: { mois },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as IssueBalance };
}

export async function ajouterCorrespondance(
  jeton: string,
  motif: string,
  genre: 'compte' | 'libelle',
  feuille: string,
): Promise<Resultat<null>> {
  const echange = await appeler('/analyse/resultat/correspondances', jeton, {
    methode: 'POST',
    corps: { motif, genre, feuille },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export async function retirerCorrespondance(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/analyse/resultat/correspondances/${id}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export async function enregistrerObjectif(
  jeton: string,
  corps: Record<string, unknown>,
): Promise<Resultat<null>> {
  const echange = await appeler('/analyse/objectifs', jeton, { methode: 'PUT', corps });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// La rentabilité — lot 102
// ---------------------------------------------------------------------------

export async function lireRentabilite(
  jeton: string,
  mois: string | null,
): Promise<Resultat<RentabiliteLue>> {
  const echange = await appeler(
    mois === null ? '/analyse/rentabilite' : `/analyse/rentabilite?mois=${mois}`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as RentabiliteLue };
}

export async function enregistrerSalaire(
  jeton: string,
  chauffeurId: string,
  mois: string,
  cout: string | null,
): Promise<Resultat<null>> {
  const echange = await appeler('/analyse/salaires', jeton, {
    methode: 'PUT',
    corps: { chauffeur_id: chauffeurId, mois, cout_employeur: cout },
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// Les indicateurs — lot 103
// ---------------------------------------------------------------------------

export async function lireIndicateurs(
  jeton: string,
): Promise<Resultat<IndicateursLus>> {
  const echange = await appeler('/analyse/indicateurs', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as IndicateursLus };
}

export async function enregistrerSeuils(
  jeton: string,
  cle: string,
  objectif: number | null,
  alerte: number | null,
): Promise<Resultat<null>> {
  const echange = await appeler(`/analyse/indicateurs/${cle}`, jeton, {
    methode: 'PUT',
    corps: { objectif, alerte },
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// L'onglet Factures — lot 106
// ---------------------------------------------------------------------------

/** Toutes les factures des deux copies Pennylane, payées comprises. */
export async function lireFactures(jeton: string): Promise<Resultat<FacturesLues>> {
  const echange = await appeler('/analyse/factures', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FacturesLues };
}

/** Écarter une facture des calculs. Le motif est obligatoire côté serveur. */
export async function ecarterFacture(
  jeton: string,
  sens: 'client' | 'fournisseur',
  pennylaneId: string,
  motif: string,
): Promise<Resultat<null>> {
  const echange = await appeler('/analyse/factures/ecartees', jeton, {
    methode: 'POST',
    corps: { sens, pennylane_id: pennylaneId, motif },
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/** Lever une exclusion. La trace reste en base, horodatée. */
export async function leverExclusion(
  jeton: string,
  sens: 'client' | 'fournisseur',
  pennylaneId: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/analyse/factures/ecartees/${sens}/${pennylaneId}`,
    jeton,
    {
      methode: 'DELETE',
    },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// Le bandeau de fiabilité — lot 108
// ---------------------------------------------------------------------------

/**
 * CE QUE LE CALCUL RETIRE. Une seule route pour les quatre écrans : chacun a
 * une forme de données différente, et quatre bandeaux bricolés sur quatre
 * formes finiraient par annoncer quatre montants différents.
 */
export async function lireFiabilite(jeton: string): Promise<Resultat<FiabiliteLue>> {
  const echange = await appeler('/analyse/fiabilite', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FiabiliteLue };
}
